import React, { useState, useCallback, useEffect } from 'react';
import CommonSelect from '../../common/select/CommonSelect';
import InputWithLabel from '../../common/InputWithLabel';
import { PlusIcon } from '@heroicons/react/24/outline';
import { useRecipeCreation } from '../../../context/RecipeCreationContext';
import { errorToast, successToast } from '../../../utils/helper';

const DIFFICULTY_LEVELS = [
  { label: 'Easy', value: 'Easy' },
  { label: 'Medium', value: 'Medium' },
  { label: 'Hard', value: 'Hard' }
];

const RECIPE_CATEGORIES = [
  { label: 'Breakfast', value: 'Breakfast' },
  { label: 'Lunch', value: 'Lunch' },
  { label: 'Dinner', value: 'Dinner' },
  { label: 'Snack', value: 'Snack' },
  { label: 'Dessert', value: 'Dessert' }
];

const COOKING_TIME_OPTIONS = [
  { label: '10 mins', value: '10' },
  { label: '15 mins', value: '15' },
  { label: '20 mins', value: '20' },
  { label: '30 mins', value: '30' },
  { label: '45 mins', value: '45' },
  { label: '60 mins', value: '60' },
  { label: '90 mins', value: '90' },
  { label: '120 mins', value: '120' }
];

const SERVING_OPTIONS = [
  { label: '1 serving', value: '1' },
  { label: '2 servings', value: '2' },
  { label: '3 servings', value: '3' },
  { label: '4 servings', value: '4' },
  { label: '5 servings', value: '5' },
  { label: '6 servings', value: '6' },
  { label: '8 servings', value: '8' },
  { label: '10 servings', value: '10' }
];

const MEASUREMENT_UNITS = [
  { label: 'Cup(s)', value: 'cup' },
  { label: 'Tablespoon(s)', value: 'tbsp' },
  { label: 'Teaspoon(s)', value: 'tsp' },
  { label: 'Gram(s)', value: 'g' },
  { label: 'Kilogram(s)', value: 'kg' },
  { label: 'Ounce(s)', value: 'oz' },
  { label: 'Pound(s)', value: 'lb' },
  { label: 'Milliliter(s)', value: 'ml' },
  { label: 'Liter(s)', value: 'l' },
  { label: 'Pinch', value: 'pinch' },
  { label: 'Unit', value: 'unit' },
  { label: 'kcal', value: 'kcal' },
  { label: 'None', value: 'none' }
];

const RecipeDetailsTab = ({ isView }) => {
  // Use context
  const { recipeData, errors, updateRecipeData } = useRecipeCreation();

  // Local state for ingredients
  const [ingredients, setIngredients] = useState(recipeData.ingredients || []);

  // Sync local ingredients state when context updates
  useEffect(() => {
    if (recipeData.ingredients) {
      setIngredients(recipeData.ingredients);
    }
  }, [recipeData.ingredients]);

  // Handle form field changes
  const handleChangeInternal = useCallback(
    (e) => {
      const { name, value, type, checked } = e.target;
      const fieldValue = type === 'checkbox' ? checked : value;

      updateRecipeData({
        [name]: fieldValue
      });
    },
    [updateRecipeData]
  );

  // Handle select field changes
  const handleSelectChangeInternal = useCallback(
    (field, selectedOption) => {
      const value = selectedOption?.value || '';
      updateRecipeData({
        [field]: value
      });
    },
    [updateRecipeData]
  );

  // Ingredient handlers
  const addIngredient = () => {
    const newIngredient = {
      id: Date.now(),
      ingredient: '',
      amount: '',
      unit: ''
    };
    const updatedIngredients = [...ingredients, newIngredient];
    setIngredients(updatedIngredients);
    updateRecipeData({ ingredients: updatedIngredients });
  };

  const updateIngredient = (id, field, value) => {
    const updatedIngredients = ingredients.map((ing) =>
      ing.id === id ? { ...ing, [field]: value } : ing
    );
    setIngredients(updatedIngredients);
    updateRecipeData({ ingredients: updatedIngredients });
  };

  return (
    <div className="space-y-6">
      {/* Recipe Details Section */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">Recipe Details</h3>

        <div className="grid lg:gap-6 gap-4 md:grid-cols-2 grid-cols-1">
          {/* Recipe Title */}
          <div>
            <InputWithLabel
              label="Recipe Title"
              placeholder="Type your recipe name here"
              onChange={handleChangeInternal}
              errorMargin="mt-1"
              labelMargin="mb-1"
              errorSize="text-[12px]"
              isLengthValidate={true}
              limit={100}
              labelClassName="text-sm font-medium text-gray-700 dark:text-shoorah-darkBg"
              value={recipeData.title}
              name="title"
              id="title"
              error={errors?.title}
              isRequired={false}
              disabled={isView}
            />
          </div>

          {/* Recipe Category */}
          <div>
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
              Recipe Category
            </label>
            <CommonSelect
              disabled={isView}
              name="category"
              onChange={(e) => handleSelectChangeInternal('category', e)}
              value={RECIPE_CATEGORIES?.find((option) => option?.value === recipeData.category)}
              options={RECIPE_CATEGORIES}
              placeholder="Select a Category"
            />
            {errors?.category && (
              <span className="text-red-500 text-[12px] mt-1 block">{errors.category}</span>
            )}
          </div>

          {/* Serving */}
          <div>
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
              Serving
            </label>
            <CommonSelect
              disabled={isView}
              name="servings"
              onChange={(e) => handleSelectChangeInternal('servings', e)}
              value={SERVING_OPTIONS?.find(
                (option) => option?.value === String(recipeData.servings)
              )}
              options={SERVING_OPTIONS}
              placeholder="No. of servings"
            />
            {errors?.servings && (
              <span className="text-red-500 text-[12px] mt-1 block">{errors.servings}</span>
            )}
          </div>

          {/* Cooking Duration */}
          <div>
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
              Cooking Duration (Mins)
            </label>
            <CommonSelect
              disabled={isView}
              name="cooking_duration"
              onChange={(e) => handleSelectChangeInternal('cooking_duration', e)}
              value={COOKING_TIME_OPTIONS?.find(
                (option) => option?.value === String(recipeData.cooking_duration)
              )}
              options={COOKING_TIME_OPTIONS}
              placeholder="Time"
            />
            {errors?.cooking_duration && (
              <span className="text-red-500 text-[12px] mt-1 block">{errors.cooking_duration}</span>
            )}
          </div>

          {/* Difficulty */}
          <div>
            <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
              Difficulty
            </label>
            <CommonSelect
              disabled={isView}
              name="difficulty"
              onChange={(e) => handleSelectChangeInternal('difficulty', e)}
              value={DIFFICULTY_LEVELS?.find((option) => option?.value === recipeData.difficulty)}
              options={DIFFICULTY_LEVELS}
              placeholder="Level of difficulty"
            />
            {errors?.difficulty && (
              <span className="text-red-500 text-[12px] mt-1 block">{errors.difficulty}</span>
            )}
          </div>
        </div>
      </div>

      {/* Ingredients Section */}
      <div className="bg-white dark:bg-shoorah-darkBgTabColor rounded-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Ingredients</h3>
          {!isView && (
            <button
              type="button"
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white flex items-center gap-1"
            >
              <span className="text-lg">✏️</span> Edit
            </button>
          )}
        </div>

        <div className="space-y-4">
          {ingredients.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <p className="mb-4">No ingredients added yet</p>
            </div>
          ) : (
            ingredients.map((ingredient, index) => (
              <div key={ingredient.id} className="grid grid-cols-12 gap-3 items-end">
                {/* Ingredient Name */}
                <div className="col-span-6">
                  {index === 0 && (
                    <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
                      Ingredient
                    </label>
                  )}
                  <input
                    type="text"
                    placeholder="Type your ingredient name here"
                    value={ingredient.ingredient}
                    onChange={(e) => updateIngredient(ingredient.id, 'ingredient', e.target.value)}
                    disabled={isView}
                    className="w-full rounded-lg px-4 py-2 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
                  />
                </div>

                {/* Amount */}
                <div className="col-span-3">
                  {index === 0 && (
                    <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
                      Amount
                    </label>
                  )}
                  <input
                    type="text"
                    placeholder="00"
                    value={ingredient.amount}
                    onChange={(e) => updateIngredient(ingredient.id, 'amount', e.target.value)}
                    disabled={isView}
                    className="w-full rounded-lg px-4 py-2 text-sm text-gray-900 dark:text-white bg-gray-50 dark:bg-shoorah-darkBgColor border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-shoorah-primary"
                  />
                </div>

                {/* Unit */}
                <div className="col-span-3">
                  {index === 0 && (
                    <label className="font-medium text-gray-700 dark:text-shoorah-darkBg mb-1 block text-sm">
                      Unit
                    </label>
                  )}
                  <CommonSelect
                    disabled={isView}
                    name={`ingredient_unit_${ingredient.id}`}
                    onChange={(e) => updateIngredient(ingredient.id, 'unit', e?.value || '')}
                    value={MEASUREMENT_UNITS?.find((option) => option?.value === ingredient.unit)}
                    options={MEASUREMENT_UNITS}
                    placeholder="Unit"
                  />
                </div>
              </div>
            ))
          )}

          {/* Add Button */}
          {!isView && (
            <button
              type="button"
              onClick={addIngredient}
              className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-full text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-shoorah-darkBgTabColor hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-shoorah-primary"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default RecipeDetailsTab;
